import type { SharedHabitPayload, SharedRoutinePayload, SharedQuestPayload } from '../types';
import type { PactSide } from './pactDirection';

/**
 * Per-side pact action refs (②e locked §0.4): creator_ref / partner_ref
 * jsonb are the source of truth for who does what. `name` is a display
 * SNAPSHOT written by the entity's owner — the partner renders it without
 * any action-table RLS widening (§0.6). type 'custom' = tracked manually
 * (no linked entity).
 */
export interface PactActionRef {
  v: 1;
  type: 'habit' | 'routine' | 'quest' | 'custom';
  id?: string;
  name?: string;
}

/** The C2-sanitized creator action ridden by creator_action_payload (§0.5). */
export interface PactActionPayload {
  v: 1;
  kind: 'habit' | 'routine' | 'quest';
  payload: SharedHabitPayload | SharedRoutinePayload | SharedQuestPayload;
}

const REF_TYPES = ['habit', 'routine', 'quest', 'custom'] as const;

export function buildActionRef(
  type: PactActionRef['type'],
  id?: string,
  name?: string
): PactActionRef {
  const ref: PactActionRef = { v: 1, type };
  if (id) ref.id = id;
  if (name) ref.name = name;
  return ref;
}

/** Parse a raw jsonb ref; tolerant of junk and legacy nulls. */
export function parseActionRef(raw: unknown): PactActionRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as { v?: unknown; type?: unknown; id?: unknown; name?: unknown };
  if (obj.v !== 1 || !REF_TYPES.includes(obj.type as never)) return null;
  return buildActionRef(
    obj.type as PactActionRef['type'],
    typeof obj.id === 'string' ? obj.id : undefined,
    typeof obj.name === 'string' ? obj.name : undefined
  );
}

export function parseActionPayload(raw: unknown): PactActionPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as { v?: unknown; kind?: unknown; payload?: unknown };
  if (obj.v !== 1 || !obj.payload || typeof obj.payload !== 'object') return null;
  if (obj.kind !== 'habit' && obj.kind !== 'routine' && obj.kind !== 'quest') return null;
  return obj as PactActionPayload;
}

/**
 * The ref for one side of a pact, with the LEGACY fallback: pre-P3 pacts
 * stored a single entity (always the creator's) in habit_id/quest_id/
 * routine_id — surface it as the creator's ref until backfill/healing.
 */
export function sideActionRef(
  pact: {
    creatorRef?: PactActionRef | null;
    partnerRef?: PactActionRef | null;
    actionType: string;
    habitId?: string;
    questId?: string;
    routineId?: string;
  },
  side: PactSide
): PactActionRef | null {
  const stored = side === 'creator' ? pact.creatorRef : pact.partnerRef;
  if (stored) return stored;
  if (side === 'partner') return null;
  if (pact.actionType === 'custom') return buildActionRef('custom');
  const legacyId = pact.habitId || pact.questId || pact.routineId;
  if (!legacyId || !['habit', 'routine', 'quest'].includes(pact.actionType)) return null;
  return buildActionRef(pact.actionType as PactActionRef['type'], legacyId);
}
